'use client';

import { c as _c } from "react/compiler-runtime";
import { Globe, InfoCircle, NavArrowRight, Printer, QrCode, UploadSquareSolid } from '@byway/icons';
import { useSearchParams } from 'next/navigation';
import pluralize from 'pluralize';
import { Image, StructuredText } from 'react-datocms';
import { Button, CardShell, CardShellContent, CardShellCTA, CardShellImage, Stack as UIStack } from 'ui';
import { shortDayShortMonth } from 'utils/DateFormats';
import { Accommodation } from '~/components/Accommodation';
import { Activity } from '~/components/Activity';
import { Card } from '~/components/Card';
import { ChipHeader } from '~/components/ChipHeader';
import { Disclosure } from '~/components/Disclosure';
import { Divider } from '~/components/Divider';
import { JourneyMapForJourneyGuide } from '~/components/Journey/JourneyMap';
import { Stack } from '~/components/Layout/Stack';
import { Leg } from '~/components/Leg';
import { Link } from '~/components/Link';
import { Location } from '~/components/Location';
import { MessagePanel } from '~/components/MessagePanel';
import { TicketingInstruction } from '~/components/TicketingInstruction';
import TravelInformationText from '~/components/TravelInformation/TravelInformationText';
import { useJourneyGuide } from '~/contexts/JourneyGuideContext';
import type { TicketingInstructionRecordFragment } from '~/libs/dato/fragments/ticketingInstructionFragment';
import type { TravelInformationDetail } from '~/libs/dato/queries/journeyGuide';
import TicketTransformer from '~/transformers/Dato/TicketTransformer';
import TimelineTransformer, { type StayTimelineItem } from '~/transformers/Dato/TimelineTransformer';
import { isNotEmptyString } from '~/utils/datoHelpers';
import { makeDirectionsURLs } from '~/utils/googleMapsDirections';
import { getTripEdgeCityName, shouldShowInterrailUkTravelCallout, type TripEdge } from '~/utils/journeyGuide/interrailUkTravelCallout';
const InterrailUkTravelCallout = t0 => {
  const $ = _c(10);
  const {
    cityName,
    edge
  } = t0;
  const t1 = `interrail-uk-travel-callout-${edge}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="shrink-0"><InfoCircle ariaLabel="Information" size="large" strokeWidth={2} /></span>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = edge === "start" ? "starting" : "ending";
  let t4;
  if ($[1] !== cityName || $[2] !== t3) {
    t4 = <p className="font-semibold underline mb-4px">Not {t3} your day in {cityName}?</p>;
    $[1] = cityName;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p>Your Interrail pass can cover the costs of your journeys to/from your local UK station, so long as you take those journeys on the same days that you'll be entering/exiting the UK on the Eurostar. In most cases, you'll be able to add the journeys to your Interrail pass and then board the trains without any requirement for added seat reservations. If needed, seat reservations can be made directly through the operator of the train you wish to travel on.</p>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t4) {
    t6 = <CardShell fill="bg-status-alert print:bg-transparent" hasBorder={true} border="border-yellow-800" isElevated={false}><div className="flex space-x-2 print:break-inside-avoid">{t2}<div className="typography-body-2">{t4}{t5}</div></div></CardShell>;
    $[5] = t4;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t1 || $[8] !== t6) {
    t7 = <div className="mt-16px" data-test-id={t1}>{t6}</div>;
    $[7] = t1;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
};
export default function JourneyGuide() {
  const $ = _c(49);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.get("summary");
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const summary = t0;
  let t1;
  if ($[2] !== searchParams) {
    t1 = searchParams?.get("print");
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const print = t1;
  const [journeyGuide] = useJourneyGuide();
  const isForPrinting = print === "true";
  const isJourneySummary = summary === "true";
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[4] !== isForPrinting || $[5] !== isJourneySummary || $[6] !== journeyGuide.allTravelInformations || $[7] !== journeyGuide.itinerary) {
    const {
      endDate,
      headerImages: overrideImages,
      interrail: isInterrail,
      interrailStaticContent,
      interrailDynamicContent,
      items,
      itineraryId,
      startDate,
      title,
      tod: isTod,
      todStaticContent,
      todDynamicContent,
      travelInformation
    } = journeyGuide.itinerary ?? {};
    const allTravelInformations = journeyGuide.allTravelInformations;
    const {
      timelineItems,
      headerImage: dynamicHeaderImage
    } = new TimelineTransformer(items ?? []).transform();
    const tickets = new TicketTransformer(items ?? []).transform();
    const headerImage = overrideImages?.[0]?.responsiveImage || dynamicHeaderImage;
    const directionsURLs = makeDirectionsURLs(timelineItems);
    let t6;
    if ($[12] !== allTravelInformations) {
      t6 = id => {
        if (!allTravelInformations) {
          return;
        }
        const info_0 = allTravelInformations.find(info => info.id === id);
        return info_0;
      };
      $[12] = allTravelInformations;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    const getGenericInfo = t6;
    let t7;
    if ($[14] !== getGenericInfo) {
      t7 = getGenericInfo("123000074");
      $[14] = getGenericInfo;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    const journeySummary = t7;
    let t8;
    if ($[16] !== getGenericInfo) {
      t8 = getGenericInfo("123049631");
      $[16] = getGenericInfo;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    const planningYourTrip = t8;
    let t9;
    if ($[18] !== getGenericInfo) {
      t9 = getGenericInfo("123049215");
      $[18] = getGenericInfo;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    const printOnlyTickets = t9;
    let t10;
    if ($[20] !== getGenericInfo) {
      t10 = getGenericInfo("161195146");
      $[20] = getGenericInfo;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    const bywayHandbook = t10;
    let t11;
    if ($[22] !== getGenericInfo) {
      t11 = getGenericInfo("bN1khMc8SyKyXUDYy0VTHw");
      $[22] = getGenericInfo;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    const interrailSummary = t11;
    let t12;
    if ($[24] !== getGenericInfo) {
      t12 = getGenericInfo("UJaWNVk_SheHdwwMq-mBmQ");
      $[24] = getGenericInfo;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    const parisMetroTickets = t12;
    let t13;
    if ($[26] !== getGenericInfo) {
      t13 = getGenericInfo("UdCU8DEMTeeRh82hcLxGxw");
      $[26] = getGenericInfo;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    const ees = t13;
    const bywayHandbookTravelInformation = travelInformation?.find(_temp);
    const startCityName = getTripEdgeCityName(timelineItems, "start");
    const endCityName = getTripEdgeCityName(timelineItems, "end");
    const showStartInterrailCallout = shouldShowInterrailUkTravelCallout({
      isInterrail,
      date: startDate,
      timelineItems,
      edge: "start"
    });
    const showEndInterrailCallout = shouldShowInterrailUkTravelCallout({
      isInterrail,
      date: endDate,
      timelineItems,
      edge: "end"
    });
    const hasPrintOnlyTickets = timelineItems.some(_temp2);
    const ticketAttachments = tickets.ticketUrls;
    let t14;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = {
        ticket: <MessagePanel message="Your tickets will be attached closer to your dates of travel." />,
        reservation: <MessagePanel message="Your reservation will be attached closer to your dates of travel." />,
        tod: <MessagePanel message="You will need to collect this ticket using the collection reference number at the top of your journey guide." />,
        todAttached: <MessagePanel message="You will need to collect this ticket using the attached Ticket Booking Confirmation." />,
        interrail: <MessagePanel message="No reservations required for this trip, just use your Interrail pass." />,
        payOnDay: <MessagePanel message="Pay the driver when you board." />,
        payAtTerminal: <MessagePanel message="Purchase your ticket on the day at the terminal." />
      };
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    const TICKET_MESSAGING = t14;
    const t15 = isJourneySummary ? <><Card colour="yellowSecondary" spaceHorizontal="--by-space-400" spaceVertical="--by-space-300"><div className="relative flex space-x-2 print:break-inside-avoid"><div className="flex items-center justify-center w-5 h-5 rounded-full shrink-0"><InfoCircle ariaLabel="Information" size="large" strokeWidth={2} /></div><div><h2 className="typography-subtitle-2">{journeySummary?.title}</h2><StructuredText data={journeySummary?.detail} /></div></div></Card>{isInterrail && interrailStaticContent && interrailDynamicContent && <div className="mt-4"><Disclosure title={interrailSummary?.title ?? ""}><TravelInformationText data={interrailSummary?.detail} /></Disclosure></div>}{bywayHandbookTravelInformation && <div id={bywayHandbook?.slug}><Card colour="blueSecondary" spaceHorizontal="--by-space-400" spaceVertical="--by-space-300"><h2 className="typography-subtitle-2 print:typography-title-6 mb-4px print:my-16px print:text-black print:break-before-page">{bywayHandbook?.title}</h2>{isForPrinting && <TravelInformationText data={bywayHandbook?.detail} />}{!isForPrinting && <div className="text-right mt-12px"><Link href={`/journey-guide/${itineraryId}/travel-information/${bywayHandbook?.slug}${isJourneySummary ? "?summary=true" : ""}`} target="_self"><Stack space="--by-space-050" variant="horizontal"><span>Find out more</span><NavArrowRight decorative={true} size="large" /></Stack></Link></div>}</Card></div>}</> : <><div className="space-y-8px pt-24px prose-p:rounded-speech-bubble print:break-inside-avoid"><StructuredText data={getGenericInfo("123037257")?.detail} /></div>{isInterrail && interrailStaticContent && interrailDynamicContent && <CardShell isFullBleed={true}><TicketingInstruction staticContent={interrailStaticContent as TicketingInstructionRecordFragment} dynamicContent={interrailDynamicContent[0]} /></CardShell>}</>;
    const t16 = !isJourneySummary && <>{isTod && todStaticContent && todDynamicContent && <CardShell isFullBleed={true}><TicketingInstruction staticContent={todStaticContent} dynamicContent={todDynamicContent[0]} /></CardShell>}<ul className="space-y-16px" aria-label="Important travel information">{travelInformation?.map((info_1, index) => <li key={index} id={info_1.slug}><Card colour="yellowSecondary" spaceHorizontal="--by-space-400" spaceVertical="--by-space-300"><h2 className="typography-subtitle-2 print:typography-title-6 mb-4px print:my-16px print:text-black print:break-before-page">{info_1.title}</h2>{isForPrinting && <TravelInformationText data={info_1.detail} />}{!isForPrinting && <div className="text-right mt-12px"><Link href={`/journey-guide/${itineraryId}/travel-information/${bywayHandbook?.slug}${isJourneySummary ? "?summary=true" : ""}`} target="_self"><Stack space="--by-space-050" variant="horizontal"><span>Find out more</span><NavArrowRight decorative={true} size="large" /></Stack></Link></div>}</Card></li>)}</ul><Card colour="yellowSecondary" spaceHorizontal="--by-space-400" spaceVertical="--by-space-300"><div className="relative flex space-x-2 print:break-inside-avoid"><div className="flex items-center justify-center w-5 h-5 rounded-full shrink-0"><InfoCircle ariaLabel="Information" size="large" strokeWidth={2} /></div><div><h2 className="typography-subtitle-2">{planningYourTrip?.title}</h2><StructuredText data={planningYourTrip?.detail} /></div></div></Card><Card colour="yellowSecondary" spaceHorizontal="--by-space-400" spaceVertical="--by-space-300"><div className="relative flex space-x-2 print:break-inside-avoid"><div className="flex items-center justify-center w-5 h-5 rounded-full shrink-0"><InfoCircle ariaLabel="Information" size="large" strokeWidth={2} /></div><div><h2 className="typography-subtitle-2">{ees?.title}</h2><StructuredText data={ees?.detail} /></div></div></Card>{!hasPrintOnlyTickets && <Card colour="yellowSecondary" spaceHorizontal="--by-space-400" spaceVertical="--by-space-300"><div className="relative grid grid-cols-[auto_1fr] gap-1.5 print:break-inside-avoid"><Printer decorative={true} size="large" /><div><h2 className="typography-subtitle-2">{printOnlyTickets?.title}</h2><StructuredText data={printOnlyTickets?.detail} /></div></div></Card>}</>;
    let t17;
    if ($[29] !== t15 || $[30] !== t16) {
      t17 = <div className="p-16px space-y-16px print:p-0">{t15}{t16}</div>;
      $[29] = t15;
      $[30] = t16;
      $[31] = t17;
    } else {
      t17 = $[31];
    }
    t5 = <div className="rounded-b-xl bg-neutral-100 print:bg-transparent">{headerImage && <div className="relative"><Image data={headerImage} priority={true} /><div aria-hidden="true" className="absolute inset-0 opacity-50 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-black" /><div className="absolute bottom-0 text-white py-4px px-16px md:py-16px"><h1 className="font-serif font-normal text-[2rem] leading-[1.2] md:typography-title-4">{title}</h1></div></div>}<JourneyMapForJourneyGuide timelineItems={timelineItems} />{t17}</div>;
    t2 = "mt-5 rounded-t-xl bg-neutral-100 print:bg-transparent";
    let t18;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = ["calendar"];
      $[32] = t18;
    } else {
      t18 = $[32];
    }
    const t19 = <CardShellContent><ChipHeader type="Start" tags={[shortDayShortMonth(startDate)]} icons={t18} /><h2 className="typography-title-6 mt-16px">{startCityName}</h2>{showStartInterrailCallout ? <InterrailUkTravelCallout cityName={startCityName} edge="start" /> : null}</CardShellContent>;
    let t20;
    if ($[33] !== t19) {
      t20 = <div className="print:break-inside-avoid"><CardShell>{t19}</CardShell></div>;
      $[33] = t19;
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    let t21;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = ["calendar"];
      $[35] = t21;
    } else {
      t21 = $[35];
    }
    const t22 = <CardShellContent><ChipHeader type="End" tags={[shortDayShortMonth(endDate)]} icons={t21} /><h2 className="typography-title-6 mt-16px">{endCityName}</h2>{showEndInterrailCallout ? <InterrailUkTravelCallout cityName={endCityName} edge="end" /> : null}</CardShellContent>;
    let t23;
    if ($[36] !== t22) {
      t23 = <div className="print:break-inside-avoid"><CardShell>{t22}</CardShell></div>;
      $[36] = t22;
      $[37] = t23;
    } else {
      t23 = $[37];
    }
    let t24;
    let t25;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <div className="flex items-center p-4 text-white bg-black rounded-full"><UploadSquareSolid decorative={true} size="large" /></div>;
      t25 = <p className="px-4 text-center sm:px-8 typography-subtitle-2">Share your travel stories and photos to help us spread the word about flight-free travel.</p>;
      $[38] = t24;
      $[39] = t25;
    } else {
      t24 = $[38];
      t25 = $[39];
    }
    const t26 = `/account/booked/${itineraryId}/upload-media`;
    let t27;
    if ($[40] !== t26) {
      t27 = <div className="print:hidden"><CardShell><CardShellContent><Card colour="yellowSecondary" isElevated={false} spaceHorizontal="--by-space-400" spaceVertical="--by-space-300"><div className="px-2 py-3"><UIStack spacing={6} align="center">{t24}{t25}<Button is="a" href={t26} type="black" size="medium" className="w-full py-4">Add my travel stories</Button></UIStack></div></Card></CardShellContent></CardShell></div>;
      $[40] = t26;
      $[41] = t27;
    } else {
      t27 = $[41];
    }
    t3 = <div className="p-16px space-y-16px print:p-0">{t20}<ol className="space-y-16px">{timelineItems.map((item_0, index_0) => {
          if (item_0.type === "Journey") {
            const isFeaturedLeg = item_0.leg?.featured === true && Boolean(item_0.leg.image?.responsiveImage);
            return <li key={`itinerary-${item_0.id}`} id={item_0.id}><CardShell>{isFeaturedLeg && !isJourneySummary && !isForPrinting && <CardShellImage><Image data={item_0?.leg?.image?.responsiveImage} className="rounded-t-lg print:hidden mb-12px" /></CardShellImage>}<CardShellContent><Leg leg={item_0} isMinimal={isJourneySummary} showChips={true} />{!isJourneySummary && <>{(item_0.ticketType === "ticket" || item_0.ticketType === "reservation") && <>{item_0.tickets && item_0.tickets.length > 0 && item_0.printTicket && isForPrinting && <MessagePanel message={`${pluralize(item_0.ticketType, item_0.tickets.length, true)} attached`} />}{item_0.tickets && item_0.tickets.length > 0 && !isJourneySummary && !isForPrinting && <Button is="a" href={`/journey-guide/${itineraryId}/tickets/${item_0.id}`} className="typography-button my-12px" block={true}><Stack space="--by-space-200" variant="horizontal">{item_0.printTicket ? <Printer decorative={true} size="large" /> : <QrCode decorative={true} size="large" />}<span>See {pluralize(item_0.ticketType, item_0.tickets.length)}</span></Stack></Button>}{item_0.tickets && item_0.tickets.length === 0 && !isJourneySummary && !isForPrinting && TICKET_MESSAGING[item_0.ticketType]}</>}{item_0.ticketType === "tod" && (item_0.tickets && item_0.tickets.length > 0 ? <>{TICKET_MESSAGING.todAttached}{isForPrinting ? <MessagePanel message={`${item_0.tickets?.length} ${pluralize("confirmation", item_0.tickets?.length)} attached`} /> : <Button is="a" href={`/journey-guide/${itineraryId}/tickets/${item_0.id}`} className="typography-button my-12px" block={true}>See Ticket Booking Confirmation</Button>}</> : TICKET_MESSAGING[item_0.ticketType])}{item_0.ticketType === "eurostar" && isNotEmptyString(item_0.bookingReference) && <><Divider variant="horizontal" /><h2 className="typography-chip-style text-neutral-600 mt-12px">Eurostar Booking Reference</h2><p className="typography-emphasis-1 my-8px">{item_0.bookingReference}</p><Divider variant="horizontal" /><Button is="a" href="https://www.eurostar.com/customer-dashboard/en/get-booking?market=uk-en" className="typography-button mt-16px" block={true} externalTab={true}><span className="mr-1.5"><Globe decorative={true} color="currentColor" size="large" /></span>Retrieve ticket</Button></>}{item_0.ticketType !== "ticket" && item_0.ticketType !== "tod" && item_0.ticketType !== "none" && item_0.ticketType !== "reservation" && TICKET_MESSAGING[item_0.ticketType]}</>}</CardShellContent></CardShell>{directionsURLs[index_0]?.transfer && <><div className="mt-12 shadow-elevation-high print:shadow-none"><a href={directionsURLs[index_0].transfer} target="_blank" rel="noreferrer"><MessagePanel message={<div className="typography-body-1"><span className="mt-2 font-bold">Transfer directions:</span>{" "}<span>{`${directionsURLs[index_0].from} to ${directionsURLs[index_0].to}`}</span></div>} status="directions" /></a></div>{directionsURLs[index_0].from?.includes("Paris") && <div className="mt-4"><Disclosure title={parisMetroTickets?.title ?? ""}><TravelInformationText data={parisMetroTickets?.detail} /></Disclosure></div>}</>}</li>;
          }
          if (item_0.type === "Stop" && isJourneySummary) {
            return;
          }
          const {
            accommodationBookings,
            activities,
            foodAndDrinkRecommendation,
            place,
            type
          } = item_0 as StayTimelineItem;
          return <li key={`itinerary-${item_0.id}`} id={item_0.id}><CardShell>{place?.images[0]?.responsiveImage && !isJourneySummary && <CardShellImage><Image data={place.images[0].responsiveImage} className="rounded-t-lg print:hidden" /></CardShellImage>}<CardShellContent><Location location={item_0} isMinimal={isJourneySummary} userSpecificDescription={item_0.type === "Stay" ? item_0.userSpecificDescription : null} />{type === "Stay" && <><Divider variant="horizontal" /><h3 className="my-24px typography-chip-style text-neutral-600" id={accommodationBookings[0].id}>Accommodation</h3><Accommodation bookings={accommodationBookings} isDetailed={isForPrinting} isMinimal={isJourneySummary} />{directionsURLs[index_0]?.arrive && <div className="mb-2 text-base font-bold typography-body-2"><a href={directionsURLs[index_0].arrive} target="_blank" rel="noreferrer"><MessagePanel message="Arrival directions" status="directions" /></a></div>}{directionsURLs[index_0]?.depart && <div className="mt-2 text-base font-bold typography-body-2"><a href={directionsURLs[index_0].depart} target="_blank" rel="noreferrer"><MessagePanel message="Departure directions" status="directions" /></a></div>}{directionsURLs[index_0] && (directionsURLs[index_0].arrive || directionsURLs[index_0].depart) && <p className="px-1 mt-3 typography-body-2">If these directions don't quite look right, please let us know in your WhatsApp support group.</p>}{!isForPrinting && !isJourneySummary && accommodationBookings[0].accommodation && <Button is="a" href={`/journey-guide/${itineraryId}/${item_0.id}`} className="w-full typography-button mb-24px mt-20px" mode="secondary">See details</Button>}</>}{!isForPrinting && !isJourneySummary && <>{activities && activities.length > 0 && <><div className="flex items-center justify-between border-t border-t-neutral-300"><h3 className="my-24px typography-chip-style text-neutral-600">Recommendations</h3><Link href={`/journey-guide/${itineraryId}/${item_0.id}`} target="_self"><Stack space="--by-space-050" variant="horizontal"><span>See all</span><NavArrowRight decorative={true} size="large" /></Stack></Link></div><ul className="mt-24px">{activities.map((activity, index_1) => <li key={`stay-activity-${item_0.id}-${activity.id}-${index_1}`} className="mb-24px" id={activity.id}><CardShell isElevated={false}><CardShellContent><Activity activity={activity} /></CardShellContent><CardShellCTA><Link href={`/journey-guide/${itineraryId}/${item_0.id}/${activity.id}`} target="_self"><Stack space="--by-space-050" variant="horizontal"><span>Learn more</span><NavArrowRight decorative={true} size="large" /></Stack></Link></CardShellCTA></CardShell></li>)}</ul></>}{foodAndDrinkRecommendation && <div className="border-t border-t-neutral-300 mt-24px"><h3 className="my-24px typography-chip-style text-neutral-600">Food and Drink in {place.placeName}</h3><div className="typography-body-1 my-48px"><TravelInformationText data={foodAndDrinkRecommendation.description} /></div></div>}</>}{!isForPrinting && !isJourneySummary && (activities && activities.length > 0 || foodAndDrinkRecommendation) && <Button is="a" href={`/journey-guide/${itineraryId}/${item_0.id}`} className="w-full typography-button mb-12px">{`See more at this ${type === "Stay" ? "stay" : "stop"}`}</Button>}</CardShellContent></CardShell></li>;
        })}</ol>{t23}{t27}</div>;
    t4 = isForPrinting && ticketAttachments && ticketAttachments.length > 0 && <ul className="mt-24px" aria-label="Ticket attachments">{ticketAttachments.map(_temp3)}</ul>;
    $[4] = isForPrinting;
    $[5] = isJourneySummary;
    $[6] = journeyGuide.allTravelInformations;
    $[7] = journeyGuide.itinerary;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[42] !== t2 || $[43] !== t3 || $[44] !== t4) {
    t6 = <div className={t2}>{t3}{t4}</div>;
    $[42] = t2;
    $[43] = t3;
    $[44] = t4;
    $[45] = t6;
  } else {
    t6 = $[45];
  }
  let t7;
  if ($[46] !== t5 || $[47] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  return t7;
}
function _temp3(ticket, index_2) {
  return <li key={index_2}><iframe src={`${ticket}?fm=png&fit=clip&h=842`} width="595px" height="842px" /></li>;
}
function _temp2(item) {
  return item.type === "Journey" && item.printTicket && item.ticketType === "ticket";
}
function _temp(t0) {
  const {
    id: id_0
  } = t0;
  return id_0 === "161195146";
}